import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Loader2, Upload, Settings, ChevronDown } from 'lucide-react';
import { MediaFileSelector } from '@/components/media/MediaFileSelector';
import { useClient } from '../../contexts/ClientContext';
import mediaService from '../../services/mediaService';
import AuthenticatedImage from '@/components/media/AuthenticatedImage';


interface ImageTo3DFormProps {
  onSubmit: (data: ImageTo3DData) => void;
  isLoading?: boolean;
  formData: ImageTo3DData;
  setFormData: React.Dispatch<React.SetStateAction<ImageTo3DData>>;
}

export interface ImageTo3DData {
  input_media_file_id: string;
  enable_pbr: boolean;
  target_polycount: number;
  texture_richness: string;
}

const TEXTURE_RICHNESS = [
  { value: 'low', label: 'Low', description: 'Faster processing, simpler textures' },
  { value: 'medium', label: 'Medium', description: 'Balanced quality and speed' },
  { value: 'high', label: 'High', description: 'Best quality, longer processing time' },
];

const POLYCOUNT_PRESETS = [
  { value: 5000, label: 'Low (5K)' },
  { value: 15000, label: 'Medium (15K)' },
  { value: 30000, label: 'High (30K)' },
  { value: 50000, label: 'Ultra (50K)' },
];

export const ImageTo3DForm: React.FC<ImageTo3DFormProps> = ({
  onSubmit,
  isLoading = false,
  formData,
  setFormData
}) => {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentClient } = useClient();

  const updateFormData = (field: keyof ImageTo3DData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageSelect = (mediaFile: any) => {
    setSelectedImage(mediaFile);
    updateFormData('input_media_file_id', mediaFile.id);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentClient) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    try {
      setIsUploading(true);
      const response = await mediaService.uploadFile(file, currentClient.id);
      
      const uploadedFile = {
        id: response.file.id,
        ...response.file
      };
      
      setSelectedImage(uploadedFile);
      updateFormData('input_media_file_id', uploadedFile.id);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      alert(error.response?.data?.detail || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };



  return (
    <div className="space-y-4">
      {/* Source Image Upload Section */}
      <div id="source-image-selection" className="group relative overflow-visible rounded-xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border-border/30 shadow-lg shadow-primary/5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="relative p-4">
          <div className="flex items-center space-x-2 mb-3">
            <div 
              className="w-7 h-7 rounded-lg flex items-center justify-center relative overflow-hidden"
              style={{ 
                background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                backgroundSize: '200% 200%',
                backgroundPosition: 'left center'
              }}
            >
              <Upload className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-base font-bold text-card-foreground">Source Image</h3>
          </div>
        
        {!selectedImage ? (
          // Show selection options when no image is selected
          <div className="grid grid-cols-2 gap-3">
            <MediaFileSelector
              onSelect={handleImageSelect}
              selectedFile={selectedImage}
              acceptedTypes={['image']}
              placeholder="Select from media"
            />
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start bg-card border-border hover:bg-accent text-card-foreground"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="mr-2 h-4 w-4" /> Upload New</>
                )}
              </Button>
            </div>
          </div>
        ) : (
          // Show upload options first, then selected image below
          <div className="space-y-3">
            {/* Change Image Options */}
            <div className="grid grid-cols-2 gap-3">
              <MediaFileSelector
                onSelect={handleImageSelect}
                selectedFile={null}
                acceptedTypes={['image']}
                placeholder="Choose different image"
              />
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start bg-card border-border hover:bg-accent text-card-foreground"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="mr-2 h-4 w-4" /> Upload Different</>
                  )}
                </Button>
              </div>
            </div>

            {/* Selected Image Preview */}
            <div className="p-4 bg-gradient-to-br from-card/50 to-muted/30 rounded-xl border border-border/50">
              <div className="flex items-start gap-4">
                <div className="relative group">
                  <AuthenticatedImage
                    src={selectedImage.thumbnail_url || selectedImage.file_url}
                    alt="Selected source image"
                    className="w-20 h-20 object-cover rounded-lg border-2 border-border/50 group-hover:border-primary/50 transition-colors"
                    clientId={currentClient.id}
                  />
                </div>
                <div className="flex-grow space-y-2">
                  <div>
                    <h4 className="text-sm font-semibold text-card-foreground">Source Image Selected</h4>
                    <p className="text-xs text-muted-foreground">Ready for 3D conversion</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-card-foreground truncate">{selectedImage.original_filename}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{selectedImage.mime_type}</span>
                      {selectedImage.file_size && (
                        <span>{mediaService.formatFileSize(selectedImage.file_size)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-muted-foreground hover:text-destructive"
              onClick={() => {
                setSelectedImage(null);
                updateFormData('input_media_file_id', '');
              }}
            >
              Remove Selected Image
            </Button>
          </div>
        )}
        </div>
      </div>

      {/* Advanced Options Section */}
      <div className="group relative overflow-visible rounded-xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border-border/30 shadow-lg shadow-primary/5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="relative p-4">
          <button
            id="advanced-options-button"
            data-tour="advanced-options"
            type="button"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center space-x-2">
              <div 
                className="w-7 h-7 rounded-lg flex items-center justify-center relative overflow-hidden"
                style={{ 
                  background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                  backgroundSize: '200% 200%',
                  backgroundPosition: 'left center'
                }}
              >
                <Settings className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-bold text-card-foreground">3D Generation Settings</h3>
            </div>
            <div className={`transform transition-transform duration-200 ${isAdvancedOpen ? 'rotate-180' : ''}`}>
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            </div>
          </button>

          {isAdvancedOpen && (
            <div id="advanced-options-3d" className="mt-4 space-y-4">
              <div className="space-y-3">
                <Label className="text-sm font-medium text-slate-900 dark:text-slate-100">Model Detail Level</Label>
                <div className="grid grid-cols-4 gap-2">
                  {POLYCOUNT_PRESETS.map((preset) => (
                    <Button
                      key={preset.value}
                      type="button"
                      variant={formData.target_polycount === preset.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateFormData('target_polycount', preset.value)}
                      className={`text-xs transition-all duration-200 ${
                        formData.target_polycount === preset.value 
                          ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600' 
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-900 dark:text-slate-100">Texture Quality</Label>
                <div className="grid grid-cols-1 gap-1">
                  {TEXTURE_RICHNESS.map((texture) => (
                    <button
                      key={texture.value}
                      type="button"
                      onClick={() => updateFormData('texture_richness', texture.value)}
                      className={`px-3 py-2 rounded-md border transition-all duration-200 text-left hover:shadow-sm ${
                        formData.texture_richness === texture.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full border flex items-center justify-center ${
                            formData.texture_richness === texture.value
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-slate-300 dark:border-slate-500'
                          }`}>
                            {formData.texture_richness === texture.value && (
                              <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                            )}
                          </div>
                          <div className={`font-medium text-sm ${
                            formData.texture_richness === texture.value
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-slate-900 dark:text-slate-100'
                          }`}>
                            {texture.label}
                          </div>
                        </div>
                        <div className={`text-xs ${
                          formData.texture_richness === texture.value
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-slate-500 dark:text-slate-400'
                        }`}>
                          {texture.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-md">
                <div className="flex items-center gap-2">
                  <Label htmlFor="enable_pbr" className="text-sm font-medium text-slate-900 dark:text-slate-100">PBR Materials</Label>
                  <span className="text-xs text-slate-500 dark:text-slate-400">Physically Based Rendering</span>
                </div>
                <Switch
                  id="enable_pbr"
                  checked={formData.enable_pbr}
                  onCheckedChange={(checked: boolean) => updateFormData('enable_pbr', checked)}
                  className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-200 dark:data-[state=unchecked]:bg-slate-600"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};